import React, { useEffect, useState } from "react";
import {
  Box, Grid, Typography, Paper, Stack, Popover, Button, TextField,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format } from "date-fns";
import api from "../../api";
import KpiCards from "./KpiCards";
import EnrollmentOverviewChart from "./EnrollmentOverviewChart";
import WorksheetCompletionDonut from "./WorksheetCompletionDonut";
import CoursePlansList from "./CoursePlansList";
import RecentCoursesTable from "./RecentCoursesTable";
import ResourceAllocationCard from "./ResourceAllocationCard";
import QuickActions from "./QuickActions";
import PlatformSummary from "./PlatformSummary";

const CARD_SX = {
  p: 2,
  height: "100%",
  borderRadius: 3,
  boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
  border: "1px solid #eef0f4",
};

const toIso = (d) => (d ? format(d, "yyyy-MM-dd") : null);

const buildRangeParams = (range) => {
  const params = {};
  if (range.from) params.from = toIso(range.from);
  if (range.to) params.to = toIso(range.to);
  return params;
};

export default function AdminDashboard() {
  const [range, setRange] = useState({ from: null, to: null });
  const [pendingRange, setPendingRange] = useState({ from: null, to: null });
  const [popAnchor, setPopAnchor] = useState(null);
  const popOpen = Boolean(popAnchor);

  const [stats, setStats] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [completion, setCompletion] = useState(null);
  const [plans, setPlans] = useState([]);
  const [recent, setRecent] = useState([]);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const params = buildRangeParams(range);
    Promise.allSettled([
      api.get("admin/dashboard/stats", { params }),
      api.get("admin/dashboard/enrollment-overview", { params }),
      api.get("admin/dashboard/worksheet-completion", { params }),
      api.get("admin/dashboard/course-plans", { params }),
      api.get("admin/dashboard/recent-courses", { params }),
      api.get("admin/dashboard/platform-summary"),
    ])
      .then((results) => {
        if (cancelled) return;
        const [s, e, w, p, r, sm] = results;
        if (s.status === "fulfilled") setStats(s.value.data);
        if (e.status === "fulfilled") setEnrollment(e.value.data);
        if (w.status === "fulfilled") setCompletion(w.value.data);
        if (p.status === "fulfilled") setPlans(p.value.data?.plans || []);
        if (r.status === "fulfilled") setRecent(r.value.data?.courses || []);
        if (sm.status === "fulfilled") setSummary(sm.value.data);
      });
    return () => { cancelled = true; };
  }, [range.from, range.to]);

  const openPopover = (event) => {
    setPendingRange(range);
    setPopAnchor(event.currentTarget);
  };
  const closePopover = () => setPopAnchor(null);
  const applyRange = () => {
    setRange(pendingRange);
    closePopover();
  };
  const clearRange = () => {
    setPendingRange({ from: null, to: null });
    setRange({ from: null, to: null });
    closePopover();
  };

  const rangeLabel = (range.from && range.to)
    ? `${format(range.from, "d MMM yyyy")} — ${format(range.to, "d MMM yyyy")}`
    : "All time";

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} mb={2}>
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ color: "#111827" }}>Dashboard</Typography>
            <Typography variant="body2" sx={{ color: "#374151", fontWeight: 700 }}>Overview of your platform activities</Typography>
          </Box>
          <Button
            onClick={openPopover}
            startIcon={<CalendarTodayIcon sx={{ fontSize: 18 }} />}
            endIcon={<ExpandMoreIcon />}
            variant="outlined"
            sx={{
              bgcolor: "white",
              borderColor: "#e5e7eb",
              color: "text.primary",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              height: 40,
              px: 1.5,
              "&:hover": { bgcolor: "#f9fafb", borderColor: "#d1d5db" },
            }}
          >
            {rangeLabel}
          </Button>
          <Popover
            open={popOpen}
            anchorEl={popAnchor}
            onClose={closePopover}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { p: 2, mt: 1 } }}
          >
            <Stack spacing={2}>
              <DatePicker
                label="From"
                value={pendingRange.from}
                onChange={(d) => setPendingRange((p) => ({ ...p, from: d }))}
                renderInput={(params) => <TextField {...params} size="small" />}
              />
              <DatePicker
                label="To"
                value={pendingRange.to}
                onChange={(d) => setPendingRange((p) => ({ ...p, to: d }))}
                renderInput={(params) => <TextField {...params} size="small" />}
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button size="small" onClick={clearRange}>All time</Button>
                <Button size="small" onClick={closePopover}>Cancel</Button>
                <Button size="small" variant="contained" onClick={applyRange} sx={{ bgcolor: "#7c3aed" }}>
                  Apply
                </Button>
              </Stack>
            </Stack>
          </Popover>
        </Stack>

        <KpiCards cards={stats?.cards} />

        <Grid container spacing={2} mt={0}>
          <Grid item xs={12} md={6} lg={5}>
            <Paper sx={CARD_SX}>
              <Typography variant="subtitle1" fontWeight={700} mb={1} sx={{ color: "#111827" }}>Enrollment Overview</Typography>
              <Box sx={{ position: "relative", minHeight: { xs: 200, sm: 240 }, width: "100%" }}>
                <EnrollmentOverviewChart data={enrollment} />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Paper sx={CARD_SX}>
              <Typography variant="subtitle1" fontWeight={700} mb={1} sx={{ color: "#111827" }}>Worksheet Completion</Typography>
              <Box sx={{ position: "relative", minHeight: { xs: 160, sm: 180 }, width: "100%" }}>
                <WorksheetCompletionDonut data={completion} />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} lg={3}>
            <Paper sx={CARD_SX}>
              <CoursePlansList plans={plans} />
            </Paper>
          </Grid>

          <Grid item xs={12} md={6} lg={5}>
            <Paper sx={CARD_SX}>
              <RecentCoursesTable rows={recent} />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <ResourceAllocationCard />
          </Grid>
          <Grid item xs={12} lg={3}>
            <Paper sx={CARD_SX}>
              <Typography variant="subtitle1" fontWeight={700} mb={1} sx={{ color: "#111827" }}>Quick Actions</Typography>
              <QuickActions />
              <Box mt={2}>
                <PlatformSummary summary={summary} />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
}
